import httpClient from "../../utils/httpClient";
import axios from "axios";
import { PERMISSIONS } from "../../constants";

export const DEFAULT_PAGE_SIZE = 100;

const getPaginatedOrgs = async (
  action,
  page = 0,
  pageSize = DEFAULT_PAGE_SIZE
) => {
  const { data: orgs } = await action({ page, pageSize });
  if (orgs.data && page < orgs.totalPages - 1) {
    const nextPageData = await getPaginatedOrgs(action, page + 1, pageSize);
    return [...orgs.data, ...nextPageData];
  }

  return orgs;
};

/* GET Linked Organizations */
export async function getLinkedOrgs() {
  const apiCall = (params) => httpClient().get("/organizations", { params });
  const fetchOrgs = getPaginatedOrgs;
  try {
    const response = await fetchOrgs(apiCall);

    if (!response) {
      throw new Error("Network response was not ok", response);
    }

    return await response;
  } catch (error) {
    console.error("Error fetching linked organizations:", error);
    throw error;
  }
}

/* GET Organizations Profile */
export async function getOrgProfile(organizationId) {
  try {
    const response = await httpClient().get(
      `/organization/${organizationId}/profile`
    );
    if (!response) {
      throw new Error("Network response was not ok", response);
    }

    return response.data;
  } catch (error) {
    console.error("Error fetching organizations profile:", error);
    throw error;
  }
}

/* Fetches Organization Member Roles */
export async function getOrganizationMemberRoles(customerIds) {
  try {
    const response = await httpClient().post("/customer/roles", {
      customerIds,
    });
    const { data } = response;

    return data;
  } catch (error) {
    console.error(
      "There was an issue fetching organization member roles",
      error
    );
    throw error;
  }
}

/* Add Role */
export async function addRole(command) {
  try {
    const response = await httpClient().post("/roles", command);
    const { data } = response;

    return data;
  } catch (error) {
    console.error("There was an issue adding the role", error);
    throw error;
  }
}

/* Remove Role */
export async function removeRole(command) {
  try {
    const { userId, roleId, organizationId, requestingUser } = command;
    const roleCommand = buildRemoveRoleCommand(
      userId,
      roleId,
      organizationId,
      requestingUser
    );
    const response = await httpClient().post("/roles", roleCommand);
    const { data } = response;

    return data;
  } catch (error) {
    console.error("There was an issue removing the role", error);
    throw error;
  }
}

function buildRemoveRoleCommand(
  customerId,
  roleId,
  organizationId,
  requestingUser
) {
  const roleCommand = {
    command: {
      RemoveRoleFromCustomer: {
        aggregateID: customerId,
        assignedRoleId: roleId,
        requestingContext: {
          requestingDomain: "MLC",
          requestingUserId: requestingUser,
          requestingOrganizationId: organizationId,
        },
      },
    },
  };

  return roleCommand;
}

/* Update Tax Exemption */
const getTaxExemptSignedUrl = (fileExtension, organizationId) =>
  httpClient().post('/tax-exemption-url', { fileExtension, organizationId })
    .then(({ data }) => data.url);

const addTaxExemptionFile = (signedUrl, file) =>
  axios.put(signedUrl, file,
    {
      headers: {
      'Content-Type': file.type
    }
  });

export async function addOrgTaxExemption({ file, organizationId }) {
  try {
    const fileExtension = file.name.substring(file.name.lastIndexOf('.') + 1);
    const signedUrl = await getTaxExemptSignedUrl(fileExtension, organizationId);

    await addTaxExemptionFile(signedUrl, file);
    return true;
  } catch (error) {
    if (error) {
      throw new Error('There was an issue uploading the tax exemption file');
    }
    return false;
  }
}

/* Update Tax Legal Name */
export async function updateTaxLegalName({ organizationId, legalName }) {
  try {
    const response = await httpClient().post('/change-legal-name', {
      organizationId,
      legalName,
    });
    const { data } = response;

    if (data.statusCode !== 200) {
      throw new Error(data?.data?.errorMessage);
    }

    return data;
  } catch (error) {
    if (error) {
      const permissionError = 'Additional permissions are needed to update this.';
      if (error.message.includes('The requesting user does not have sufficient permissions to perform this action')) {
        throw new Error(permissionError);
      } else {
        throw new Error('The legal name cannot be updated right now. Please try later.');
      }
    }
  }
}

/* Update Organization Info */
export async function updateOrganizationInfo(payload) {
  try {
    const response = await httpClient().post(
      "/customer/organization-info",
      payload
    );
    const { data } = response;

    return data;
  } catch (error) {
    console.error("There was an issue updating the organization info", error);
    throw error;
  }
}

/* Update Customer Phone */
export async function updateCustomerPhone(payload) {
  try {
    const response = await httpClient().post("/customer/update-phone", payload);
    const { data } = response;

    return data;
  } catch (error) {
    console.error("There was an issue updating the customer phone", error);
    throw error;
  }
}

/* Update Customer Email */
export async function updateCustomerEmail(payload) {
  try {
    const response = await httpClient().post("/customer/update-email", payload);
    const { data } = response;

    return data;
  } catch (error) {
    console.error("There was an issue updating the customer email", error);
    throw error;
  }
}

/* Create Customer Phone */
export async function createCustomerPhone(payload) {
  try {
    const response = await httpClient().post("/customer/create-phone", payload);
    const { data } = response;

    return data;
  } catch (error) {
    console.error("There was an issue creating the customer phone", error);
    throw error;
  }
}

/* Create Customer Email */
export async function createCustomerEmail(payload) {
  try {
    const response = await httpClient().post("/customer/create-email", payload);
    const { data } = response;

    return data;
  } catch (error) {
    console.error("There was an issue creating the customer email", error);
    throw error;
  }
}

// Fetches permissions for a specific organization and customer.
// Accepts:
// - customerId
// - org: the full organization profile (includes organizationId and billingAccounts)
// - displayIds: an array of permission displayIds to check

export const getPermissionsForOrg = async (
  customerId,
  organizationId,
  displayIds = []
) => {
  // Filter the full permission config by the displayIds provided
  const permissionsToCheck = displayIds.length
    ? PERMISSIONS.filter((p) => displayIds.includes(p.displayId))
    : PERMISSIONS;

  // Format each permission with the current organization scope
  const queryPermissions = permissionsToCheck.map((permission) => ({
    permissionId: permission.permissionId,
    permissionScope: { organization: organizationId },
  }));

  const response = await httpClient().post(
    "/organization/customer/permissions",
    {
      customerId,
      queryPermissions,
    }
  );

  return response.data;
};

/* GET PCO Groups */
export async function getPCOGroups(routeToken) {
  try {
    const response = await httpClient().get(`/pco/${routeToken}/groups`);
    const { data } = response;

    return data;
  } catch (error) {
    console.error('There was an issue fetching pco groups', error);
    throw error;
  }
}

/* GET PCO Group Details */
export async function getPCOGroupDetails(routeToken, groupId) {
  try {
    const response = await httpClient().get(
      `/pco/${routeToken}/groups/${groupId}`
    );
    const { data } = response;

    const responsePayload = {
      members: data,
      groupId
    };

    return responsePayload;
  } catch (error) {
    console.error('There was an issue fetching pco group details', error);
    throw error;
  }
}

/* POST Import PCO Group */
export async function importPCOGroup({routeToken, groupId, accountId}) {
  try {
    const response = await httpClient().post(
      `/pco/${routeToken}/groups/${groupId}/import?accountId=${accountId}`
    );
    const { data } = response;

    return data;
  } catch (error) {
    console.error('There was an issue syncing your pco group', error);
    throw error;
  }
}
